// ============================================================
// AlertsStatusBanner.tsx — ADMIN-ONLY: "Deadline alerts are not running on
// this server". The server only starts its scheduled jobs (deadline /
// limitation alerts, the "no limitation date" nudge, reminders, backups) in
// production mode; GET /api/system/alerts-status says whether it did. Asked
// once per sign-in; shown above every screen until the admin hides it for
// this browser session. Shows nothing while loading, on an error, or for
// non-admins (the endpoint is admin-only too).
// ★ (Audit) "Hide for now" is per SIGNED-IN USER: the flag is keyed by the
// user's id, so another admin signing in on the same browser session still
// sees the warning (it used to be hidden for whoever came next).
// ============================================================
import React, { useEffect, useState } from 'react'
import { getAlertsStatusApi } from '../../api/casevoApi'
import { useCasevoStore } from '../../store/useCasevoStore'
import ActionBox from './ActionBox'

const hideKey = (userId: string | null): string => `casevo_alerts_banner_hidden:${userId ?? 'anon'}`
export const readAlertsBannerHidden = (userId: string | null): boolean => {
  try { return sessionStorage.getItem(hideKey(userId)) === '1' } catch { return false }
}

const AlertsStatusBanner: React.FC = () => {
  const isAdmin = useCasevoStore((s) => s.authUser?.role === 'admin')
  const userId = useCasevoStore((s) => s.authUser?.id ?? null)
  const [running, setRunning] = useState<boolean | null>(null)
  const [hidden, setHidden] = useState(() => readAlertsBannerHidden(userId))
  // A different user signed in → their own choice.
  useEffect(() => { setHidden(readAlertsBannerHidden(userId)) }, [userId])

  useEffect(() => {
    setRunning(null)
    if (!isAdmin) return undefined
    let cancelled = false
    getAlertsStatusApi()
      .then((r) => { if (!cancelled) setRunning(r.running) })
      .catch(() => { /* unknown → no banner (never a false alarm) */ })
    return () => { cancelled = true }
  }, [isAdmin, userId])

  if (!isAdmin || running !== false || hidden) return null
  return (
    // The top-bar position (a band above every screen) wraps the shared slim
    // ActionBox shell — red `danger`, role=alert, "Hide for now" as its link.
    <div className="px-4 sm:px-6 pt-3" data-alerts-banner="">
      <ActionBox variant="slim" tone="danger" alert
        title="Deadline alerts are not running on this server."
        message="Limitation and deadline alerts, reminders and backups are off because the server isn’t in production mode. Ask whoever runs the server to check its NODE_ENV setting."
        actions={[{ label: 'Hide for now', kind: 'link', onClick: () => { setHidden(true); try { sessionStorage.setItem(hideKey(userId), '1') } catch { /* ignore */ } } }]} />
    </div>
  )
}

export default AlertsStatusBanner
